/**
 * Sets full page SEO for a product detail page:
 *   - <title>
 *   - <meta name="description">
 *   - Open Graph + Twitter Card
 *   - Canonical link
 *   - schema.org Product JSON-LD (with AggregateRating + Offer)
 *
 * All managed in-place: re-rendering on a different product replaces the
 * existing tags rather than appending. Search engines that execute JS pick
 * this up fine; SSR is a follow-up.
 */

export interface ProductSEOInput {
  productId: string | number;
  productName: string;
  productSlug: string;
  description?: string;
  imageUrl?: string;
  price: number;
  currency?: string;
  inStock: boolean;
  brandName?: string;
  storeName?: string;
  averageRating?: number; // 0–5
  ratingCount?: number;
  reviewCount?: number;
}

const META_KEY = 'data-seo-managed';
const JSONLD_ID = 'product-jsonld';

/**
 * Upsert a meta tag matching the managed selector. If a non-managed tag with
 * the same key already lives in <head> (e.g. the static description from
 * index.html), reclaim it instead of creating a duplicate — Google picks up
 * whichever appears first, so duplicates make per-product SEO unreliable.
 */
function upsertMeta(
  selector: string,
  attrs: Record<string, string>,
  unmanagedSelector?: string,
): void {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el && unmanagedSelector) {
    el = document.head.querySelector<HTMLMetaElement>(unmanagedSelector);
  }
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  el.setAttribute(META_KEY, 'true');
  for (const [k, v] of Object.entries(attrs)) {
    el.setAttribute(k, v);
  }
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"][${META_KEY}]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(META_KEY, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function truncate(s: string, n: number): string {
  if (!s) return '';
  return s.length <= n ? s : s.slice(0, n - 1).trimEnd() + '…';
}

export function setProductSEO(input: ProductSEOInput): void {
  if (typeof document === 'undefined') return;

  const {
    productId,
    productName,
    productSlug,
    description,
    imageUrl,
    price,
    currency = 'MYR',
    inStock,
    brandName,
    storeName,
    averageRating,
    ratingCount,
    reviewCount,
  } = input;

  const url =
    typeof window !== 'undefined'
      ? `${window.location.origin}/p/${productSlug}`
      : `/p/${productSlug}`;

  const title = storeName
    ? `${productName} — ${storeName}`
    : productName;

  const desc = truncate(
    description?.replace(/\s+/g, ' ').trim() || `${productName} on ${storeName || 'our store'}.`,
    160,
  );

  document.title = title;

  // Standard meta. Pass the unmanaged-fallback selector so we reclaim the
  // static description from index.html instead of duplicating it.
  upsertMeta(
    `meta[name="description"][${META_KEY}]`,
    { name: 'description', content: desc },
    `meta[name="description"]:not([${META_KEY}])`,
  );

  // Open Graph
  upsertMeta(`meta[property="og:type"][${META_KEY}]`, {
    property: 'og:type',
    content: 'product',
  });
  upsertMeta(`meta[property="og:title"][${META_KEY}]`, {
    property: 'og:title',
    content: title,
  });
  upsertMeta(`meta[property="og:description"][${META_KEY}]`, {
    property: 'og:description',
    content: desc,
  });
  upsertMeta(`meta[property="og:url"][${META_KEY}]`, {
    property: 'og:url',
    content: url,
  });
  if (imageUrl) {
    upsertMeta(`meta[property="og:image"][${META_KEY}]`, {
      property: 'og:image',
      content: imageUrl,
    });
  }
  if (storeName) {
    upsertMeta(`meta[property="og:site_name"][${META_KEY}]`, {
      property: 'og:site_name',
      content: storeName,
    });
  }

  // Twitter Card
  upsertMeta(`meta[name="twitter:card"][${META_KEY}]`, {
    name: 'twitter:card',
    content: imageUrl ? 'summary_large_image' : 'summary',
  });
  upsertMeta(`meta[name="twitter:title"][${META_KEY}]`, {
    name: 'twitter:title',
    content: title,
  });
  upsertMeta(`meta[name="twitter:description"][${META_KEY}]`, {
    name: 'twitter:description',
    content: desc,
  });
  if (imageUrl) {
    upsertMeta(`meta[name="twitter:image"][${META_KEY}]`, {
      name: 'twitter:image',
      content: imageUrl,
    });
  }

  upsertLink('canonical', url);

  // schema.org Product JSON-LD
  const jsonld: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productName,
    sku: String(productId),
    url,
    image: imageUrl ? [imageUrl] : undefined,
    description: desc,
    brand: brandName || storeName
      ? { '@type': 'Brand', name: brandName || storeName }
      : undefined,
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency: currency,
      price: price.toFixed(2),
      availability: inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
    },
  };

  if (averageRating != null && ratingCount && ratingCount > 0) {
    jsonld.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Number(averageRating).toFixed(1),
      reviewCount: reviewCount ?? ratingCount,
      ratingCount,
      bestRating: '5',
      worstRating: '1',
    };
  }

  let script = document.getElementById(JSONLD_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = JSONLD_ID;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(jsonld, (_, v) =>
    v === undefined ? undefined : v,
  );
}

/**
 * Cleanup — call on unmount so non-product routes don't carry stale tags.
 * Leaves <title> alone (next route owns it) but clears Product JSON-LD.
 */
export function clearProductSEO(): void {
  if (typeof document === 'undefined') return;
  const script = document.getElementById(JSONLD_ID);
  if (script) script.remove();
}
